export const isNativeHlsSupported = () => {
  var video = document.createElement("video");
  return video.canPlayType('application/x-mpegURL; codecs="avc1.42E01E"');
};

export const isHlsPluginSupported = () => {
  const mediaSource = window.MediaSource || window.WebKitMediaSource;
  const sourceBuffer = window.SourceBuffer || window.WebKitSourceBuffer;
  const isTypeSupported =
    mediaSource &&
    typeof mediaSource.isTypeSupported === "function" &&
    mediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');

  const sourceBufferValidAPI =
    !sourceBuffer ||
    (sourceBuffer.prototype &&
      typeof sourceBuffer.prototype.appendBuffer === "function" &&
      typeof sourceBuffer.prototype.remove === "function");
  return !!isTypeSupported && !!sourceBufferValidAPI;
};

export const getVideoSrcFallbacks = (src, context) => {
  const srcs = [];
  const qualityVal = [6, 2, 3, 4, 5];

  if (!context.features.phone) {
    qualityVal.unshift(1);
  }

  if (src) {
    for (let elem = 0; elem < qualityVal.length; elem++) {
      const mp4Src = src.replace(/(_0)?\.m3u8/, `_${qualityVal[elem]}.mp4`);
      srcs.push(`${mp4Src}#t=0.1`);
    }
  }
  return srcs;
};

export const getVideoSrc = props => {
  const {
    src,
    videoAttributes: { url, timestamp, notPristine },
    context,
  } = props;
  const ts = timestamp || context?.lastStaticSyncTimestamp;
  let videoSrc = src || url;

  if (videoSrc) {
    if (!/http/.test(videoSrc) && context) {
      videoSrc = `${context.videoBaseUrl}/${videoSrc}`;
    }
    if (!/m3u8|mp4/.test(videoSrc)) {
      videoSrc = `${videoSrc}${!notPristine ? "_0" : ""}.m3u8`;
    }
    if (!/ts=/.test(videoSrc) && ts) {
      const [src, queryString] = videoSrc.split("?");
      videoSrc = `${src}${!queryString?.length ? "?" : "&"}ts=${ts}`;
    }
    videoSrc = videoSrc.replace(/_0_0.m3u8/, "_0.m3u8");
    if (/\.mp4/.test(videoSrc)) {
      // thumbnail as poster from frame of video. Is needed for safari ios in order to make them playable
      videoSrc += "#t=0.1";
    }
  }
  return { videoSrc, fallbacks: getVideoSrcFallbacks(videoSrc, context) };
};

const loadVideo = async (props, node) => {
  const { videoSrc, fallbacks } = getVideoSrc(props);

  if (!/.mp4/.test(videoSrc) && node && isHlsPluginSupported()) {
    const { default: Hls } = await import("hls.js");
    const hls = new Hls({
      debug: false,
      capLevelToPlayerSize: true,
      enableWorker: false,
      maxMaxBufferLength: 30,
    });

    await hls.on(Hls.Events.MANIFEST_PARSED, function(_, data) {
      const targetWidth = (node.offsetWidth || window.screen.availWidth) * window.devicePixelRatio;
      const { capLevel } = data.levels.reduce(
        (qualities, curr, currentIndex) => {
          qualities.capLevel =
            Math.abs(curr.width - targetWidth) < Math.abs(data.levels[qualities.capLevel].width - targetWidth)
              ? currentIndex
              : qualities.capLevel;
          if (curr.bitrate >= data.levels[qualities.bestLevel].bitrate) {
            qualities.bestLevel = currentIndex;
          }
          return qualities;
        },
        { capLevel: 0, bestLevel: 0 }
      );
      if (props.attrs?.singleQuality || props.singleQuality) {
        data.levels.forEach((level, index) => {
          if (index !== capLevel) {
            hls.removeLevel(index);
          }
        });
      } else {
        hls.autoLevelCapping = capLevel;
        hls.startLevel = capLevel;
        hls.firstLevel = capLevel;
      }
    });
    hls.loadSource(videoSrc);
    hls.attachMedia(node);
    return {
      type: "application/x-mpegURL",
      src: videoSrc,
      hls,
      fallbacks,
    };
  } else {
    return {
      src: videoSrc,
      type: "video/mp4",
      fallbacks,
    };
  }
};

export default loadVideo;
